import Text from '@/components/ui/custom/dataDisplay/Text';
import Klass from '@/models/Klass';
import { Trans, useTranslation } from 'react-i18next';
import AvatarClassroom from '@/components/klass/AvatarClassroom/AvatarClassroom';
import { klassLeaderBoardQueryKeys } from '@/data/services/querykeys';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { handleUserFullName } from '@/functions/handleUserFullName';
import { LeaderBoardRank } from '@/models/LeaderBoardRank';
import { twJoin } from 'tailwind-merge';
import { Avatar } from '@/components/ui/custom/dataDisplay/Avatar';
import InfinityList from '@/components/ui/custom/InfinityList';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { LoadingIcon } from '@/components/icons';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/utils/routes';

type Props = {
  klass: Klass | undefined;
};

export default function KlassLeaderBoard({ klass }: Props) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'dashboard.dashboardClass',
  });

  if (!klass) return null;

  return (
    <div className="flex flex-col self-start md:flex-row xl:flex-col bg-base-100 rounded-xl shadow-md p-2 gap-2 xl:max-w-sm xl:sticky xl:top-4 xl:max-h-[calc(100vh-10rem)] overflow-hidden">
      <div className="flex flex-col gap-3.5 md:w-1/2 xl:w-full">
        <Text
          text={t('yourClass')}
          as="h3"
          format="poppins-600"
          className="text-20"
        />
        <AvatarClassroom
          className={{ base: 'rounded-t-2xl rounded-b-2xl' }}
          klass={klass}
        />
      </div>

      <Leaderboard klass={klass} />
    </div>
  );
}

type LeaderboardProps = {
  klass: Klass;
};

function Leaderboard({ klass }: LeaderboardProps) {
  const {
    results: leaderboard,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isInitialLoading: isLoading,
  } = useInfiniteService({
    ...klassLeaderBoardQueryKeys.list(klass.id)._ctx.infinity,
  });

  if (isLoading)
    return <LoadingIcon className="w-10 h-10 mx-auto my-2.5 text-primary" />;

  return (
    <div className="flex flex-col gap-2.5 md:w-1/2 xl:w-full overflow-hidden">
      <Text
        text="Leaderboard"
        as="h3"
        format="poppins-600"
        className="text-20"
      />

      <InfinityList
        className={twJoin(
          'flex flex-col gap-2.5 h-min max-h-80 xl:max-h-[calc(100vh-34%)] overflow-y-auto',
          scrollBarYClassName,
        )}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onReachEnd={fetchNextPage}
      >
        {leaderboard.map(userLeaderboard => (
          <li key={userLeaderboard.id}>
            <RankCard userLeaderboard={userLeaderboard} />
          </li>
        ))}
      </InfinityList>
    </div>
  );
}

function RankCard({ userLeaderboard }: { userLeaderboard: LeaderBoardRank }) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'leaderboard',
  });
  const isFirstPlace = userLeaderboard.rank === 1;
  return (
    <Link to={ROUTES.PROFILE.INFO(userLeaderboard.username)}>
      <div className="bg-base-200 rounded-lg py-2 px-1.5 flex justify-between items-center">
        <div className="flex gap-2.5 items-center">
          <Avatar
            size="7"
            userId={userLeaderboard.id}
            className={twJoin(
              'border-2',
              isFirstPlace ? 'border-warning' : 'border-accent',
            )}
          />
          <div className="flex flex-col">
            <Text
              className="text-neutral"
              text={handleUserFullName(userLeaderboard)}
            />
            <Text
              text={t('classXp', { xp: userLeaderboard.xpAmount })}
              className="text-12 text-base-content/70"
            />
          </div>
        </div>
        <ConditionalRenderer condition={userLeaderboard.xpAmount !== 0}>
          <Position position={userLeaderboard.rank} />
        </ConditionalRenderer>
      </div>
    </Link>
  );
}

function Position({ position }: { position: number }) {
  const isFirstPlace = position === 1;

  return (
    <p
      className={twJoin(
        'h-7 w-7 font-rubik font-500 rounded-full flex items-center justify-center',
        isFirstPlace
          ? 'bg-warning text-base-100'
          : 'bg-accent-content text-accent',
      )}
    >
      <Trans
        i18nKey={`leaderboard.rank.position`}
        values={{ count: position, ordinal: true }}
        components={{
          sup: <sup className="text-10" />,
        }}
      />
    </p>
  );
}
